import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import HeroSection from '@/pages/home/components/HeroSection';
import HotRecommend from '@/pages/home/components/HotRecommend';
import CategoryShowcase from '@/pages/home/components/CategoryShowcase';
import LatestUpdates from '@/pages/home/components/LatestUpdates';
import NewsletterSection from '@/pages/home/components/NewsletterSection';

export default function Home() {
  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        <HeroSection />

        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 pt-6">
          <Breadcrumb items={[{ label: '首页', href: '/' }]} />
        </div>

        <HotRecommend />
        <CategoryShowcase />
        <LatestUpdates />

        <section className="py-14 md:py-20 px-4 md:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                为什么选择红桃影院
              </h2>
              <p className="text-sm text-foreground-500 mt-2">为您打造极致追剧体验</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-4k-line', title: '高清画质', desc: '支持1080P与4K超清播放，每一个画面都清晰细腻，给您影院级观影体验。' },
                { icon: 'ri-flashlight-line', title: '极速更新', desc: '热播剧集同步更新，第一时间上线最新内容，永远快人一步。' },
                { icon: 'ri-global-line', title: '海量资源', desc: '汇聚国产剧、韩剧、美剧、日剧等海量资源，5000+部剧集任您选择。' },
                { icon: 'ri-device-line', title: '多端同步', desc: '支持手机、平板、电脑多端在线观看，随时随地畅享追剧乐趣。' },
              ].map((feature, index) => (
                <div key={index} className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                    <i className={`${feature.icon} text-2xl text-primary-600`}></i>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 lg:px-8 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500 mt-2">关于红桃影院的常见疑问解答</p>
            </div>

            <div className="space-y-4">
              {[
                { q: '红桃影院需要付费吗？', a: '红桃影院所有电视剧资源完全免费在线观看，无需任何付费订阅，也没有隐藏收费项目。我们致力于为追剧爱好者提供优质的免费影视内容。' },
                { q: '支持哪些设备观看？', a: '红桃影院全面支持手机（iOS/Android）、平板电脑（iPad/安卓平板）、笔记本电脑和台式机等多种设备在线观看，自适应屏幕尺寸，随时随地畅享追剧。' },
                { q: '电视剧更新频率如何？', a: '热播剧集我们会在播出后第一时间同步更新，国产剧、韩剧通常24小时内上线最新集数。美剧和日剧也会在播出后及时更新，确保您不错过任何精彩内容。' },
                { q: '支持哪些地区的电视剧？', a: '红桃影院覆盖中国大陆国产剧、韩国韩剧、美国美剧、日本日剧、港台剧等全球热门电视剧资源，满足不同用户的追剧需求。' },
              ].map((faq, index) => (
                <details key={index} className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
                  <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                    <h3 className="text-base font-medium text-foreground-900 pr-4">{faq.q}</h3>
                    <i className="ri-add-line text-foreground-400 group-open:hidden flex-none"></i>
                    <i className="ri-subtract-line text-foreground-400 hidden group-open:block flex-none"></i>
                  </summary>
                  <div className="px-6 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <NewsletterSection />
      </main>

      <Footer />

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: '红桃影院 - 电视剧全集免费追',
          description: '红桃影视为您汇聚电视剧全集免费资源，最新电视剧、热播剧集同步更新，国产剧全集、韩剧在线看一网打尽。',
          url: 'https://hongtao.tv/',
          isPartOf: { '@type': 'WebSite', name: '红桃影院', url: 'https://hongtao.tv' },
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            { '@type': 'Question', name: '红桃影院需要付费吗？', acceptedAnswer: { '@type': 'Answer', text: '红桃影院所有电视剧资源完全免费在线观看，无需任何付费订阅，也没有隐藏收费项目。' } },
            { '@type': 'Question', name: '支持哪些设备观看？', acceptedAnswer: { '@type': 'Answer', text: '红桃影院全面支持手机、平板电脑、笔记本电脑和台式机等多种设备在线观看。' } },
            { '@type': 'Question', name: '电视剧更新频率如何？', acceptedAnswer: { '@type': 'Answer', text: '热播剧集播出后第一时间同步更新，国产剧、韩剧通常24小时内上线最新集数。' } },
            { '@type': 'Question', name: '支持哪些地区的电视剧？', acceptedAnswer: { '@type': 'Answer', text: '覆盖中国大陆国产剧、韩国韩剧、美国美剧、日本日剧、港台剧等全球热门电视剧资源。' } },
          ],
        })}
      </script>
    </>
  );
}